import {
    Box,
    Paper,
    Typography,
    IconButton,
    Button,
    TextField,
    List,
    ListItem,
    ListItemText,
    Divider,
    Stack,
    Collapse,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import {
    Close as CloseIcon,
    Delete as DeleteIcon,
    KeyboardArrowDown,
    KeyboardArrowUp,
    CalculateOutlined,
} from '@mui/icons-material';
import { useSumator } from '../contexts/SumatorContext';
import { useTranslation } from 'react-i18next';

const SumatorWidget = () => {
    const { t } = useTranslation();
    const {
        isOpen,
        setIsOpen,
        collapsed,
        setCollapsed,
        lines,
        removeLine,
        clearSumator,
        primit,
        setPrimit,
        multiClient,
        setMultiClient,
    } = useSumator();

    if (!isOpen) return null;

    const total = lines.reduce((sum, line) => sum + line.amount, 0);
    const absTotal = Math.abs(total);
    const isDePlatit = total < 0;
    const isDeIncasat = total > 0;
    const rest = primit !== null ? primit - absTotal : 0;

    let totalLabel = t('sumator.total');
    let totalLabelColor = 'text.primary';
    let totalAmountColor = 'primary';
    if (isDeIncasat) {
        totalLabel = t('sumator.toPay');
        totalLabelColor = 'success.main';
        totalAmountColor = 'success.main';
    } else if (isDePlatit) {
        totalLabel = t('sumator.toReceive');
        totalLabelColor = 'error.main';
        totalAmountColor = 'error.main';
    }

    return (
        <Paper
            data-testid="sumator-widget"
            elevation={6}
            sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                width: 320,
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 2
            }}
        >
            <Box
                sx={{
                    p: 1,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalculateOutlined />
                    <Typography variant="subtitle1" fontWeight="bold">
                        {t('sumator.title')}
                    </Typography>
                </Box>
                <Box>
                    <IconButton size="small" color="inherit" onClick={() => setCollapsed(!collapsed)}>
                        {collapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                    <IconButton size="small" color="inherit" onClick={() => setIsOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            <Collapse in={!collapsed}>
                <Box sx={{ p: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={multiClient}
                                    onChange={event => setMultiClient(event.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('sumator.multiClient')}
                                </Typography>
                            }
                        />
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={clearSumator}
                            sx={{ minWidth: '40px', p: 0.5 }}
                            title={t('sumator.clear')}
                        >
                            S
                        </Button>
                    </Box>

                    <Paper
                        variant="outlined"
                        sx={{
                            height: 150,
                            overflow: 'auto',
                            mb: 2,
                            bgcolor: '#fafafa',
                            borderRadius: 1
                        }}
                    >
                        {lines.length === 0 ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ p: 2, textAlign: 'center' }}
                            >
                                {t('sumator.noLines')}
                            </Typography>
                        ) : (
                            <List dense disablePadding>
                                {lines.map(line => (
                                    <ListItem
                                        key={line.id}
                                        divider
                                        sx={{ py: 0.5 }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                onClick={() => removeLine(line.id)}
                                            >
                                                <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={line.description}
                                            slotProps={{
                                                primary: {
                                                    variant: 'body2',
                                                    noWrap: true,
                                                    title: line.description,
                                                },
                                            }}
                                            sx={{ maxWidth: '60%' }}
                                        />
                                        <ListItemText
                                            primary={new Intl.NumberFormat('ro-RO', {
                                                style: 'currency',
                                                currency: 'RON',
                                                maximumFractionDigits: 0,
                                            }).format(line.amount)}
                                            slotProps={{
                                                primary: {
                                                    variant: 'body2',
                                                    fontWeight: 'bold',
                                                    align: 'right',
                                                },
                                            }}
                                            sx={{ pr: 3 }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>

                    <Stack spacing={1.5}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                color={totalLabelColor}
                            >
                                {totalLabel}
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                color={totalAmountColor}
                            >
                                {new Intl.NumberFormat('ro-RO', {
                                    style: 'currency',
                                    currency: 'RON',
                                    maximumFractionDigits: 0,
                                }).format(absTotal)}
                            </Typography>
                        </Box>

                        {isDeIncasat && (
                            <>
                                <Divider />
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        label={t('sumator.received')}
                                        size="small"
                                        type="number"
                                        fullWidth
                                        value={primit ?? ''}
                                        onChange={event => {
                                            const value = event.target.value;
                                            setPrimit(value === '' ? null : Number(value));
                                        }}
                                        slotProps={{
                                            input: {
                                                sx: { fontWeight: 'bold', bgcolor: '#fff' },
                                            },
                                        }}
                                    />
                                    <TextField
                                        label={t('sumator.change')}
                                        size="small"
                                        fullWidth
                                        value={new Intl.NumberFormat('ro-RO').format(rest)}
                                        slotProps={{
                                            input: {
                                                readOnly: true,
                                                sx: {
                                                    fontWeight: 'bold',
                                                    bgcolor: '#f5f5f5',
                                                    color:
                                                        rest < 0
                                                            ? 'error.main'
                                                            : 'success.main',
                                                },
                                            },
                                        }}
                                    />
                                </Box>
                            </>
                        )}
                    </Stack>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default SumatorWidget;
